import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import {
  FaDollarSign, FaFileInvoiceDollar, FaReceipt, FaChartLine,
  FaMoneyCheckAlt, FaExclamationTriangle, FaArrowRight, FaChartPie,
  FaTools, FaBuilding
} from 'react-icons/fa';
import DashboardLayout from '../../layouts/DashboardLayout';
import Select from '../../components/shared/Select';
import { getFinancialSummary } from '../../services/dashboardService';
import { fetchInvoices } from './financialService';
import { formatCurrency } from '../../utils/utils';
import LoadingSpinner from '../../components/shared/LoadingSpinner';

const Page = styled.div`
  padding-bottom: 32px;
  color: ${p => p.theme.colors.textPrimary};
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  flex-wrap: wrap;
  gap: 12px;
  h1 {
    font-size: 22px;
    display: flex;
    align-items: center;
    gap: 10px;
    color: ${p => p.theme.colors.textPrimary};
    margin: 0;
  }
  @media (max-width: 600px) {
    h1 { font-size: 18px; }
  }
`;

const SectionTitle = styled.h2`
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: ${p => p.theme.colors.textSecondary};
  opacity: 0.75;
  margin: 28px 0 12px;
  display: flex;
  align-items: center;
  gap: 8px;
  svg { color: ${p => p.theme.colors.primary}; }
  &:first-of-type { margin-top: 4px; }
`;

const MetricsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(170px, 1fr));
  gap: 12px;
  margin-bottom: 4px;
  @media (max-width: 600px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;
  }
`;

const MetricCard = styled.div`
  background: ${p => p.theme.colors.surface};
  border-radius: 14px;
  padding: 16px 18px;
  border: 1px solid rgba(255,255,255,0.05);
  box-shadow: 0 2px 10px rgba(0,0,0,0.25);
  position: relative;
  overflow: hidden;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
  &::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 3px;
    background: ${p => p.accent || p.theme.colors.primary};
  }
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 16px rgba(0,0,0,0.35);
  }
  .label {
    font-size: 10px;
    color: ${p => p.theme.colors.textSecondary};
    opacity: 0.7;
    text-transform: uppercase;
    letter-spacing: 0.6px;
    font-weight: 600;
  }
  .value {
    font-size: 22px;
    font-weight: 700;
    color: ${p => p.valueColor || p.theme.colors.textPrimary};
    margin-top: 6px;
    letter-spacing: -0.3px;
    line-height: 1.15;
    word-break: break-word;
  }
  .sub {
    font-size: 11px;
    color: ${p => p.theme.colors.textSecondary};
    opacity: 0.55;
    margin-top: 3px;
  }
  @media (max-width: 600px) {
    padding: 14px;
    border-radius: 12px;
    .value { font-size: 18px; }
    .label { font-size: 9px; }
  }
`;

const CardSection = styled.div`
  background: ${p => p.theme.colors.surface};
  border-radius: 16px;
  border: 1px solid rgba(255,255,255,0.05);
  box-shadow: 0 2px 12px rgba(0,0,0,0.25);
  padding: 22px;
  margin-bottom: 14px;
  @media (max-width: 600px) {
    padding: 16px;
    border-radius: 14px;
  }
`;

const CardSectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
  flex-wrap: wrap;
  h3 {
    font-size: 15px;
    font-weight: 600;
    display: flex;
    align-items: center;
    gap: 8px;
    color: ${p => p.theme.colors.textPrimary};
    margin: 0;
    svg { color: ${p => p.theme.colors.primary}; }
  }
  @media (max-width: 600px) {
    h3 { font-size: 14px; }
  }
`;

const HeaderTotal = styled.span`
  font-size: 14px;
  font-weight: 700;
  color: ${p => p.color || p.theme.colors.textPrimary};
  background: rgba(255,255,255,0.04);
  padding: 4px 10px;
  border-radius: 999px;
  white-space: nowrap;
`;

const ViewLink = styled.span`
  color: ${p => p.theme.colors.primary};
  font-size: 12px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  font-weight: 600;
  &:hover { text-decoration: underline; }
`;

const BarRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  column-gap: 12px;
  row-gap: 4px;
  padding: 8px 0;
  font-size: 13px;
  border-bottom: 1px solid rgba(255,255,255,0.04);
  &:last-of-type { border-bottom: none; }
`;

const BarLabel = styled.div`
  grid-column: 1 / 2;
  grid-row: 1 / 2;
  display: flex;
  align-items: center;
  gap: 6px;
  color: ${p => p.theme.colors.textPrimary};
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
  @media (max-width: 600px) { font-size: 12px; }
`;

const BarTrack = styled.div`
  grid-column: 1 / 2;
  grid-row: 2 / 3;
  height: 8px;
  background: rgba(255,255,255,0.06);
  border-radius: 999px;
  overflow: hidden;
`;

const BarFill = styled.div`
  height: 100%;
  background: ${p => p.color || p.theme.colors.primary};
  border-radius: 999px;
  width: ${p => p.width || '0%'};
  min-width: ${p => (p.width && p.width !== '0%') ? '3px' : '0'};
  transition: width 0.4s ease;
`;

const BarValue = styled.div`
  grid-column: 2 / 3;
  grid-row: 1 / 3;
  align-self: center;
  text-align: right;
  font-weight: 700;
  font-size: 14px;
  color: ${p => p.theme.colors.textPrimary};
  white-space: nowrap;
  .pct {
    display: block;
    font-size: 10px;
    font-weight: 500;
    color: ${p => p.theme.colors.textSecondary};
    opacity: 0.65;
    margin-top: 2px;
  }
  @media (max-width: 600px) { font-size: 13px; }
`;

const SourceTag = styled.span`
  display: inline-block;
  font-size: 9px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  padding: 2px 6px;
  border-radius: 4px;
  background: rgba(255,255,255,0.06);
  color: ${p => p.theme.colors.textSecondary};
  opacity: 0.8;
`;

const TotalRow = styled(BarRow)`
  margin-top: 10px;
  padding-top: 12px;
  border-top: 1px solid rgba(255,255,255,0.08);
  border-bottom: none;
  ${BarLabel} { font-weight: 700; color: ${p => p.theme.colors.textPrimary}; }
`;

const GrandTotalRow = styled(BarRow)`
  margin-top: 6px;
  padding-top: 12px;
  border-top: 2px solid ${p => p.theme.colors.primary};
  border-bottom: none;
  ${BarLabel} { font-weight: 700; color: ${p => p.theme.colors.primary}; }
  ${BarValue} { color: ${p => p.theme.colors.primary}; }
`;

const InvoiceRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 12px 0;
  border-bottom: 1px solid rgba(255,255,255,0.05);
  gap: 10px;
  cursor: pointer;
  transition: background 0.15s ease;
  &:hover {
    background: rgba(255,255,255,0.03);
    margin: 0 -12px;
    padding: 12px;
    border-radius: 10px;
  }
  &:last-child { border-bottom: none; }
`;

const InvoiceInfo = styled.div`
  flex: 1; min-width: 0;
`;

const InvoiceName = styled.div`
  font-weight: 600;
  font-size: 14px;
  color: ${p => p.theme.colors.textPrimary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const InvoiceSub = styled.div`
  font-size: 12px;
  color: ${p => p.theme.colors.textSecondary};
  opacity: 0.7;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const InvoiceAmount = styled.div`
  text-align: right;
  flex-shrink: 0;
`;

const InvoiceAmountVal = styled.div`
  font-weight: 700;
  font-size: 14px;
  color: ${p => p.theme.colors.textPrimary};
`;

const InvoiceStatusBadge = styled.span`
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  padding: 3px 8px;
  border-radius: 999px;
  color: white;
  display: inline-block;
  margin-top: 4px;
`;

const EmptyText = styled.p`
  color: ${p => p.theme.colors.textSecondary};
  opacity: 0.6;
  font-size: 13px;
  text-align: center;
  padding: 16px 0;
  margin: 0;
`;

const statusColors = {
  'Draft': '#6c757d', 'Sent': '#007bff', 'Partially Paid': '#F69620',
  'Paid': '#28a745', 'Overdue': '#dc3545', 'Cancelled': '#6c757d'
};

const costCategoryColors = {
  'Products': '#e91e63', 'Materials': '#9c27b0', 'Crew Hours': '#3f51b5',
  'Maintenance': '#00bcd4', 'Others': '#607d8b', 'Tools': '#ff9800', 'Fuel': '#4caf50'
};

const expenseCategoryColors = {
  'Advertisement': '#e91e63', 'Insurance': '#9c27b0', 'Marketing': '#673ab7',
  'Licensing & Fees': '#3f51b5', 'Office Supplies': '#2196f3', 'Vehicle': '#00bcd4',
  'Vehicle Fuel': '#0288d1', 'Equipment Fuel': '#0097a7', 'Maintenance': '#8bc34a',
  'COGS': '#795548', 'General Labor / Helper': '#ff5722',
  'Software & Subscriptions': '#009688', 'Rent & Utilities': '#4caf50',
  'Professional Services': '#ff9800', 'Other': '#607d8b'
};

const FinancialDashboard = () => {
  const navigate = useNavigate();
  const [metrics, setMetrics] = useState(null);
  const [recentInvoices, setRecentInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('30days');

  useEffect(() => { loadData(); }, [period]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [summaryResult, invoicesResult] = await Promise.allSettled([
        getFinancialSummary({ period }),
        fetchInvoices()
      ]);
      if (summaryResult.status === 'fulfilled') setMetrics(summaryResult.value);
      if (invoicesResult.status === 'fulfilled') setRecentInvoices(invoicesResult.value.slice(0, 8));
    } catch (error) {
      console.error('Error loading financial data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getBarWidth = (value, max) => {
    if (!max || max === 0) return '0%';
    return Math.min((value / max) * 100, 100) + '%';
  };

  if (loading) return <DashboardLayout><LoadingSpinner /></DashboardLayout>;

  const m = metrics || {};

  return (
    <DashboardLayout>
      <Page>
      <Header>
        <h1><FaChartLine /> Financial Overview</h1>
        <Select label="Period" value={period} onChange={(e) => setPeriod(e.target.value)}>
          <option value="7days">Last 7 Days</option>
          <option value="30days">Last 30 Days</option>
          <option value="60days">Last 60 Days</option>
          <option value="90days">Last 90 Days</option>
          <option value="12months">Last 12 Months</option>
        </Select>
      </Header>

      {/* TOP METRICS */}
      <SectionTitle><FaDollarSign /> Summary</SectionTitle>
      <MetricsRow>
        <MetricCard accent="#28a745" valueColor="#28a745">
          <div className="label">Revenue (Completed)</div>
          <div className="value">{formatCurrency(m.totalRevenue)}</div>
        </MetricCard>
        <MetricCard accent="#007bff" valueColor="#007bff">
          <div className="label">Payments Received</div>
          <div className="value">{formatCurrency(m.totalPaymentsReceived)}</div>
        </MetricCard>
        <MetricCard accent="#F69620" valueColor="#F69620">
          <div className="label">Outstanding</div>
          <div className="value">{formatCurrency(m.totalUnpaidInvoices)}</div>
          <div className="sub">Unpaid invoices</div>
        </MetricCard>
        <MetricCard accent="#dc3545" valueColor="#dc3545">
          <div className="label">Total Costs</div>
          <div className="value">{formatCurrency(m.totalAllCosts)}</div>
          <div className="sub">Project + Business</div>
        </MetricCard>
        <MetricCard accent={parseFloat(m.trueProfit) >= 0 ? '#28a745' : '#dc3545'} valueColor={parseFloat(m.trueProfit) >= 0 ? '#28a745' : '#dc3545'}>
          <div className="label">Net Profit</div>
          <div className="value">{formatCurrency(m.trueProfit)}</div>
        </MetricCard>
      </MetricsRow>

      {/* QUOTES PIPELINE */}
      <SectionTitle><FaChartLine /> Quotes Pipeline</SectionTitle>
      <MetricsRow>
        <MetricCard accent="#007bff" valueColor="#007bff">
          <div className="label">Sent</div>
          <div className="value">{formatCurrency(m.totalSentAmount)}</div>
          <div className="sub">{m.totalSent || 0} quotes</div>
        </MetricCard>
        <MetricCard accent="#28a745" valueColor="#28a745">
          <div className="label">Accepted</div>
          <div className="value">{formatCurrency(m.totalAcceptedAmount)}</div>
          <div className="sub">{m.totalAccepted || 0} quotes</div>
        </MetricCard>
        <MetricCard accent="#dc3545" valueColor="#dc3545">
          <div className="label">Declined</div>
          <div className="value">{formatCurrency(m.totalDeclinedAmount)}</div>
          <div className="sub">{m.totalDeclined || 0} quotes</div>
        </MetricCard>
        <MetricCard accent="#3f51b5" valueColor="#3f51b5">
          <div className="label">Invoiced</div>
          <div className="value">{formatCurrency(m.totalInvoiced)}</div>
          <div className="sub">{m.totalInvoiceCount || 0} invoices</div>
        </MetricCard>
      </MetricsRow>

      {/* INVOICING SUMMARY */}
      <SectionTitle><FaFileInvoiceDollar /> Invoicing</SectionTitle>
      <MetricsRow>
        <MetricCard accent="#3f51b5">
          <div className="label">Total Invoiced</div>
          <div className="value">{formatCurrency(m.totalInvoiced)}</div>
          <div className="sub">{m.totalInvoiceCount || 0} invoices</div>
        </MetricCard>
        <MetricCard accent="#28a745">
          <div className="label">Paid</div>
          <div className="value">{formatCurrency(m.invoicesByStatus?.Paid?.total || 0)}</div>
          <div className="sub">{m.invoicesByStatus?.Paid?.count || 0} invoices</div>
        </MetricCard>
        <MetricCard accent="#F69620">
          <div className="label">Partially Paid</div>
          <div className="value">{formatCurrency(m.invoicesByStatus?.['Partially Paid']?.total || 0)}</div>
          <div className="sub">{m.invoicesByStatus?.['Partially Paid']?.count || 0} invoices</div>
        </MetricCard>
        <MetricCard accent="#007bff">
          <div className="label">Sent (Awaiting)</div>
          <div className="value">{formatCurrency(m.invoicesByStatus?.Sent?.total || 0)}</div>
          <div className="sub">{m.invoicesByStatus?.Sent?.count || 0} invoices</div>
        </MetricCard>
      </MetricsRow>

      {/* PAYMENTS RECEIVED */}
      {m.paymentsByMethod?.length > 0 && (() => {
        const totalPay = parseFloat(m.totalPaymentsReceived) || 0;
        const maxPayment = Math.max(...m.paymentsByMethod.map(x => parseFloat(x.total) || 0));
        const pct = (v) => totalPay > 0 ? ((v / totalPay) * 100).toFixed(1) + '%' : '0%';
        return (
        <CardSection>
          <CardSectionHeader>
            <h3><FaMoneyCheckAlt /> Payments Received</h3>
            <HeaderTotal color="#84c959">{formatCurrency(totalPay)}</HeaderTotal>
          </CardSectionHeader>
          {m.paymentsByMethod.map((p, i) => {
            const v = parseFloat(p.total) || 0;
            return (
              <BarRow key={i}>
                <BarLabel>
                  {p.method}
                  <SourceTag>{p.count}</SourceTag>
                </BarLabel>
                <BarTrack>
                  <BarFill color="#84c959" width={getBarWidth(v, maxPayment)} />
                </BarTrack>
                <BarValue>
                  {formatCurrency(v)}
                  <span className="pct">{pct(v)}</span>
                </BarValue>
              </BarRow>
            );
          })}
        </CardSection>
        );
      })()}

      {/* JOB COSTS BY CATEGORY */}
      {m.jobCostsByCategory?.length > 0 && (() => {
        const jobTotal = parseFloat(m.totalCosts) || 0;
        const maxCost = Math.max(...m.jobCostsByCategory.map(x => parseFloat(x.total) || 0));
        const pct = (v) => jobTotal > 0 ? ((v / jobTotal) * 100).toFixed(1) + '%' : '0%';
        return (
        <CardSection>
          <CardSectionHeader>
            <h3><FaTools /> Job Costs</h3>
            <HeaderTotal color="#FF5252">{formatCurrency(jobTotal)}</HeaderTotal>
          </CardSectionHeader>
          {m.jobCostsByCategory.map((c, i) => {
            const v = parseFloat(c.total) || 0;
            return (
              <BarRow key={i}>
                <BarLabel>{c.category}</BarLabel>
                <BarTrack>
                  <BarFill color={costCategoryColors[c.category] || '#607d8b'} width={getBarWidth(v, maxCost)} />
                </BarTrack>
                <BarValue>
                  {formatCurrency(v)}
                  <span className="pct">{pct(v)}</span>
                </BarValue>
              </BarRow>
            );
          })}
          <TotalRow>
            <BarLabel>Total</BarLabel>
            <BarTrack><BarFill color="#FF5252" width="100%" /></BarTrack>
            <BarValue>{formatCurrency(jobTotal)}</BarValue>
          </TotalRow>
        </CardSection>
        );
      })()}

      {/* BUSINESS COSTS BY CATEGORY */}
      {m.expensesByCategory?.length > 0 && (() => {
        const bizTotal = parseFloat(m.totalBusinessExpenses) || 0;
        const maxExp = Math.max(...m.expensesByCategory.map(x => parseFloat(x.total) || 0));
        const pct = (v) => bizTotal > 0 ? ((v / bizTotal) * 100).toFixed(1) + '%' : '0%';
        return (
        <CardSection>
          <CardSectionHeader>
            <h3><FaBuilding /> Business Costs</h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <HeaderTotal color="#FF5252">{formatCurrency(bizTotal)}</HeaderTotal>
              <ViewLink onClick={() => navigate('/financial/expenses')}>View All <FaArrowRight /></ViewLink>
            </div>
          </CardSectionHeader>
          {m.expensesByCategory.map((e, i) => {
            const v = parseFloat(e.total) || 0;
            return (
              <BarRow key={i}>
                <BarLabel>{e.category}</BarLabel>
                <BarTrack>
                  <BarFill color={expenseCategoryColors[e.category] || '#607d8b'} width={getBarWidth(v, maxExp)} />
                </BarTrack>
                <BarValue>
                  {formatCurrency(v)}
                  <span className="pct">{pct(v)}</span>
                </BarValue>
              </BarRow>
            );
          })}
          <TotalRow>
            <BarLabel>Total</BarLabel>
            <BarTrack><BarFill color="#FF5252" width="100%" /></BarTrack>
            <BarValue>{formatCurrency(bizTotal)}</BarValue>
          </TotalRow>
        </CardSection>
        );
      })()}

      {/* COMBINED COSTS */}
      {((m.jobCostsByCategory?.length || 0) + (m.expensesByCategory?.length || 0)) > 0 && (() => {
        const jobTotal = parseFloat(m.totalCosts) || 0;
        const bizTotal = parseFloat(m.totalBusinessExpenses) || 0;
        const grandTotal = jobTotal + bizTotal;
        const combined = [
          ...(m.jobCostsByCategory || []).map(c => ({
            category: c.category,
            total: parseFloat(c.total) || 0,
            source: 'Job',
            color: costCategoryColors[c.category] || '#3f51b5'
          })),
          ...(m.expensesByCategory || []).map(c => ({
            category: c.category,
            total: parseFloat(c.total) || 0,
            source: 'Business',
            color: expenseCategoryColors[c.category] || '#607d8b'
          }))
        ].sort((a, b) => b.total - a.total);
        const pct = (v) => grandTotal > 0 ? ((v / grandTotal) * 100).toFixed(1) + '%' : '0%';

        return (
          <CardSection>
            <CardSectionHeader>
              <h3><FaChartPie /> Total Costs</h3>
              <HeaderTotal color="#FF5252">{formatCurrency(grandTotal)}</HeaderTotal>
            </CardSectionHeader>
            {combined.map((c, i) => (
              <BarRow key={`${c.source}-${c.category}-${i}`}>
                <BarLabel title={`${c.category} (${c.source})`}>
                  {c.category}
                  <SourceTag>{c.source}</SourceTag>
                </BarLabel>
                <BarTrack>
                  <BarFill color={c.color} width={pct(c.total)} />
                </BarTrack>
                <BarValue>
                  {formatCurrency(c.total)}
                  <span className="pct">{pct(c.total)}</span>
                </BarValue>
              </BarRow>
            ))}
            <TotalRow>
              <BarLabel>Job Costs</BarLabel>
              <BarTrack>
                <BarFill color="#3f51b5" width={grandTotal > 0 ? ((jobTotal / grandTotal) * 100) + '%' : '0%'} />
              </BarTrack>
              <BarValue>
                {formatCurrency(jobTotal)}
                <span className="pct">{pct(jobTotal)}</span>
              </BarValue>
            </TotalRow>
            <BarRow>
              <BarLabel style={{ fontWeight: 700 }}>Business Costs</BarLabel>
              <BarTrack>
                <BarFill color="#607d8b" width={grandTotal > 0 ? ((bizTotal / grandTotal) * 100) + '%' : '0%'} />
              </BarTrack>
              <BarValue>
                {formatCurrency(bizTotal)}
                <span className="pct">{pct(bizTotal)}</span>
              </BarValue>
            </BarRow>
            <GrandTotalRow>
              <BarLabel>Grand Total</BarLabel>
              <BarTrack><BarFill color="#F69620" width="100%" /></BarTrack>
              <BarValue>{formatCurrency(grandTotal)}</BarValue>
            </GrandTotalRow>
          </CardSection>
        );
      })()}

      {/* RECENT INVOICES */}
      <CardSection>
        <CardSectionHeader>
          <h3><FaFileInvoiceDollar /> Recent Invoices</h3>
          <ViewLink onClick={() => navigate('/financial/invoices')}>View All <FaArrowRight /></ViewLink>
        </CardSectionHeader>
        {recentInvoices.length > 0 ? recentInvoices.map(inv => {
          const remaining = parseFloat(inv.totalAmount) - parseFloat(inv.paidAmount);
          return (
            <InvoiceRow key={inv.id} onClick={() => navigate(`/financial/invoices/${inv.id}`)}>
              <InvoiceInfo>
                <InvoiceName>{inv.billToName || inv.Lead?.name || `Lead #${inv.leadId}`}</InvoiceName>
                <InvoiceSub>{inv.invoiceNumber} · Quote #{inv.estimateId}{inv.Lead?.city ? ` · ${inv.Lead.city}` : ''}</InvoiceSub>
                <InvoiceSub>{inv.Lead?.email}</InvoiceSub>
              </InvoiceInfo>
              <InvoiceAmount>
                <InvoiceAmountVal>{formatCurrency(inv.totalAmount)}</InvoiceAmountVal>
                {parseFloat(inv.paidAmount) > 0 && (
                  <InvoiceSub>Paid: {formatCurrency(inv.paidAmount)} / Due: {formatCurrency(remaining)}</InvoiceSub>
                )}
                <InvoiceStatusBadge style={{ backgroundColor: statusColors[inv.status] }}>{inv.status}</InvoiceStatusBadge>
              </InvoiceAmount>
            </InvoiceRow>
          );
        }) : (
          <EmptyText>No invoices yet.</EmptyText>
        )}
      </CardSection>
      </Page>
    </DashboardLayout>
  );
};

export default FinancialDashboard;
